import { DashboardDatasetKey } from '@features/dashboard/interfaces/dashboard.interfaces';
import { DropdownOption } from '@ntv360/component-pantry';
import { SVG_ICONS } from '@shared/constants';

/**
 * Chart display type for the switch-graph dropdown.
 */
export type DashboardChartType = 'line' | 'bar';

/**
 * All 12 calendar months for the Month dropdown.
 */
export const MONTH_OPTIONS: DropdownOption[] = [
    { label: 'Months', value: -1 },
    { label: 'January', value: 0 },
    { label: 'February', value: 1 },
    { label: 'March', value: 2 },
    { label: 'April', value: 3 },
    { label: 'May', value: 4 },
    { label: 'June', value: 5 },
    { label: 'July', value: 6 },
    { label: 'August', value: 7 },
    { label: 'September', value: 8 },
    { label: 'October', value: 9 },
    { label: 'November', value: 10 },
    { label: 'December', value: 11 },
];

/**
 * Switch Graph dropdown: Basic Line Chart / Group Bar Chart.
 */
export const CHART_TYPE_OPTIONS: DropdownOption[] = [
    { label: 'Basic Line Chart', value: 'line' },
    { label: 'Group Bar Chart', value: 'bar' },
];

/**
 * Configuration structure for the overview statistics cards.
 */
export interface StatCardConfig {
    /** Human-readable title for the card. */
    title: string;
    /** The dataset key this card represents (used for lookup and selection). */
    datasetKey: DashboardDatasetKey;
    /** Identifier for the SVG icon to display. */
    iconKey: string;
    /** Optional color overrides for themed cards (e.g. Total Licenses). */
    customColor?: { iconColor: string; bgColor: string };
}

/**
 * Configuration for the license status metrics displayed in the graph header.
 */
export interface LicenseMetricConfig {
    /** Target property key in the license metrics object. */
    key: 'online' | 'offline' | 'pending' | 'demo' | 'inactive' | 'assigned' | 'unassigned';
    /** Human-readable label for the metric. */
    label: string;
    /** Specific BEM modifier class for targeting colors. */
    modifier: string;
}

/**
 * Configuration for the legend items in the growth chart.
 */
export interface LegendItemConfig {
    /** Label to show in the legend. */
    label: string;
    /** Specific BEM modifier class for color application. */
    modifier: string;
}

/**
 * Static configuration for the four primary dashboard overview cards.
 */
export const STAT_CARDS_CONFIG: StatCardConfig[] = [
    {
        title: 'Total Dealers',
        datasetKey: 'dealers',
        iconKey: SVG_ICONS.dealers,
    },
    {
        title: 'Total Licenses',
        datasetKey: 'licenses',
        iconKey: SVG_ICONS.licenses,
        customColor: { iconColor: '#FFFFFF', bgColor: '#8DCB2C' },
    },
    {
        title: 'Total Users',
        datasetKey: 'users',
        iconKey: SVG_ICONS.users,
    },
    {
        title: 'Total Assets',
        datasetKey: 'assets',
        iconKey: SVG_ICONS.mediaLibrary,
    },
];

/**
 * Ordered configuration for the detailed license status metrics.
 */
export const LICENSE_METRIC_CONFIGS: LicenseMetricConfig[] = [
    { key: 'online', label: 'Online', modifier: 'online' },
    { key: 'offline', label: 'Offline', modifier: 'offline' },
    { key: 'pending', label: 'Pending', modifier: 'pending' },
    { key: 'demo', label: 'Demo', modifier: 'demo' },
    { key: 'inactive', label: 'Inactive', modifier: 'inactive' },
    { key: 'assigned', label: 'Assigned', modifier: 'assigned' },
    { key: 'unassigned', label: 'Unassigned', modifier: 'unassigned' },
];

/**
 * List of legend items for the chart.
 */
export const GRAPH_LEGEND_ITEMS: LegendItemConfig[] = [
    { label: 'Growth', modifier: 'growth' },
    { label: 'Downturn', modifier: 'downturn' },
];
